package org.lennycodes.gradematewebsite.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.onMouseEnter
import com.varabyte.kobweb.compose.ui.modifiers.onMouseLeave
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.icons.fa.FaIcon
import com.varabyte.kobweb.silk.components.icons.fa.IconCategory
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.lennycodes.gradematewebsite.components.style.CardStyle
import org.lennycodes.gradematewebsite.components.style.EyebrowStyle
import org.lennycodes.gradematewebsite.components.style.GradeMateColors
import org.lennycodes.gradematewebsite.components.style.HighlightTileGridStyle
import org.lennycodes.gradematewebsite.components.style.SecondaryButtonStyle
import org.lennycodes.gradematewebsite.components.style.SectionHeadingStyle
import org.lennycodes.gradematewebsite.components.style.SectionInnerStyle
import org.lennycodes.gradematewebsite.components.style.SectionStyle
import org.lennycodes.gradematewebsite.components.style.toRgba
import org.lennycodes.gradematewebsite.util.Platform
import org.w3c.dom.events.Event
import kotlin.time.Duration.Companion.milliseconds

private const val Ease = "cubic-bezier(0.22, 1, 0.36, 1)"
private const val EaseOutBack = "cubic-bezier(0.34, 1.56, 0.64, 1)"

private fun prefersReducedMotion() = window.matchMedia("(prefers-reduced-motion: reduce)").matches

private fun detectPlatformLabel(): String? {
    val ua = window.navigator.userAgent.lowercase()
    return when {
        "android" in ua -> "Android"
        "iphone" in ua || "ipad" in ua -> "iOS"
        "windows" in ua -> "Windows"
        "mac os" in ua || "macintosh" in ua -> "macOS"
        "linux" in ua || "x11" in ua -> "Linux"
        else -> null
    }
}

private fun platformIcon(label: String): Pair<String, IconCategory> = when (label.lowercase()) {
    "android" -> "android" to IconCategory.BRAND
    "windows" -> "windows" to IconCategory.BRAND
    "linux" -> "linux" to IconCategory.BRAND
    "macos", "ios" -> "apple" to IconCategory.BRAND
    else -> "desktop" to IconCategory.SOLID
}

@Composable
private fun Reveal(
    id: String,
    modifier: Modifier = Modifier,
    delayMs: Int = 0,
    content: @Composable () -> Unit
) {
    var visible by remember { mutableStateOf(false) }
    val reduce = remember { prefersReducedMotion() }

    DisposableEffect(Unit) {
        val check = {
            val top = document.getElementById(id)?.getBoundingClientRect()?.top
            if (reduce || (top != null && top < window.innerHeight * 0.88)) visible = true
        }
        val onScroll: (Event) -> Unit = { check() }
        window.addEventListener("scroll", onScroll)
        check()
        onDispose { window.removeEventListener("scroll", onScroll) }
    }

    Box(
        modifier
            .id(id)
            .fillMaxWidth()
            .styleModifier {
                val d = if (reduce) 0 else delayMs
                val dur = if (reduce) 0 else 900
                property("scroll-margin-top", "104px")
                property("opacity", if (visible) "1" else "0")
                property("transform", if (visible) "translateY(0)" else "translateY(32px)")
                property(
                    "transition",
                    "opacity ${dur}ms ease ${d}ms, transform ${dur}ms $Ease ${d}ms"
                )
            }
    ) {
        content()
    }
}

// ---------------------------------------------------------------------------
// Sub-heading
// ---------------------------------------------------------------------------
@Composable
private fun SubHeading(number: String, title: String, subtitle: String? = null) {
    Column(Modifier.fillMaxWidth().gap(6.px)) {
        Div(
            Modifier
                .styleModifier {
                    property("display", "flex")
                    property("align-items", "center")
                    property("gap", "14px")
                }.toAttrs()
        ) {
            Span(
                Modifier
                    .styleModifier {
                        property("display", "inline-flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                        property("min-width", "34px")
                        property("height", "26px")
                        property("padding", "0 10px")
                        property("border-radius", "8px")
                        property(
                            "background-image",
                            "linear-gradient(135deg, ${GradeMateColors.Primary}, #4f46e5)"
                        )
                        property("color", "#ffffff")
                        property("font-size", "11px")
                        property("font-weight", "800")
                        property("letter-spacing", "0.08em")
                        property("font-variant-numeric", "tabular-nums")
                        property(
                            "box-shadow",
                            "0 6px 14px -6px ${GradeMateColors.Primary.toRgba(alpha = 0.6f)}"
                        )
                    }.toAttrs()
            ) {
                Text(number)
            }

            P(
                Modifier
                    .margin(0.px)
                    .fontSize(24.px)
                    .fontWeight(FontWeight.Bold)
                    .color(GradeMateColors.TextPrimary)
                    .styleModifier {
                        property("letter-spacing", "-0.025em")
                        property("line-height", "1.15")
                    }.toAttrs()
            ) {
                Text(title)
            }
        }

        if (subtitle != null) {
            P(
                Modifier
                    .margin(0.px)
                    .fontSize(14.px)
                    .lineHeight(1.65)
                    .color(GradeMateColors.TextSecondary)
                    .styleModifier {
                        property("max-width", "720px")
                        property("opacity", "0.82")
                    }.toAttrs()
            ) {
                Text(subtitle)
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Quick-jump pill — uses A() so the whole pill is clickable and can hold
// both the label and the animated arrow.
// ---------------------------------------------------------------------------
@Composable
private fun DocsPill(text: String, href: String) {
    var hovered by remember { mutableStateOf(false) }
    val reduce = remember { prefersReducedMotion() }
    fun t(ms: Int): Int = if (reduce) 0 else ms

    A(
        href = href,
        attrs = Modifier
            .onMouseEnter { hovered = true }
            .onMouseLeave { hovered = false }
            .styleModifier {
                property("display", "inline-flex")
                property("align-items", "center")
                property("gap", "8px")
                property("padding", "9px 16px 9px 18px")
                property("border-radius", "999px")
                property("text-decoration", "none")
                property("font-size", "13px")
                property("font-weight", "600")
                property("letter-spacing", "-0.005em")
                property(
                    "color",
                    if (hovered) "#ffffff" else GradeMateColors.TextSecondary.toRgba()
                )
                property(
                    "background-color",
                    if (hovered) GradeMateColors.Primary.toString()
                    else GradeMateColors.Background.toRgba()
                )
                property(
                    "border",
                    "1px solid ${
                        if (hovered) GradeMateColors.Primary.toRgba()
                        else GradeMateColors.Border.toRgba()
                    }"
                )
                property(
                    "box-shadow",
                    if (hovered) "0 12px 22px -10px ${GradeMateColors.Primary.toRgba(alpha = 0.5f)}"
                    else "none"
                )
                property("transform", if (hovered) "translateY(-2px)" else "translateY(0)")
                property(
                    "transition",
                    "transform ${t(250)}ms $Ease, " +
                            "background-color ${t(220)}ms ease, " +
                            "border-color ${t(220)}ms ease, " +
                            "color ${t(220)}ms ease, " +
                            "box-shadow ${t(250)}ms ease"
                )
            }
            .toAttrs()
    ) {
        Text(text)

        Span(
            Modifier
                .styleModifier {
                    property("display", "inline-flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                    property("width", "14px")
                    property("height", "14px")
                    property("opacity", if (hovered) "1" else "0")
                    property(
                        "transform",
                        if (hovered) "translateX(0)" else "translateX(-6px)"
                    )
                    property(
                        "transition",
                        "opacity ${t(200)}ms ease, transform ${t(250)}ms $Ease"
                    )
                }.toAttrs()
        ) {
            FaIcon(
                name = "arrow-right",
                style = IconCategory.SOLID,
                modifier = Modifier.fontSize(9.px)
            )
        }
    }
}

// ---------------------------------------------------------------------------
// Highlight tile
// ---------------------------------------------------------------------------
@Composable
private fun HighlightTile(iconName: String, label: String) {
    var hovered by remember { mutableStateOf(false) }
    val reduce = remember { prefersReducedMotion() }
    fun t(ms: Int) = if (reduce) 0 else ms

    Div(
        Modifier
            .onMouseEnter { hovered = true }
            .onMouseLeave { hovered = false }
            .styleModifier {
                property("display", "flex")
                property("align-items", "center")
                property("gap", "14px")
                property("padding", "16px 18px")
                property("border-radius", "16px")
                property(
                    "background-image",
                    "linear-gradient(150deg, ${GradeMateColors.Surface}, rgba(248, 250, 252, 0.92))"
                )
                property(
                    "border",
                    "1px solid ${
                        if (hovered) GradeMateColors.Primary.toRgba(alpha = 0.35f)
                        else GradeMateColors.Border.toRgba()
                    }"
                )
                property(
                    "transform",
                    if (hovered) "translateY(-4px)" else "translateY(0)"
                )
                property(
                    "box-shadow",
                    if (hovered)
                        "0 20px 36px -22px ${GradeMateColors.Primary.toRgba(alpha = 0.55f)}, " +
                                "inset 0 1px 0 rgba(255,255,255,0.9)"
                    else "inset 0 1px 0 rgba(255,255,255,0.7)"
                )
                property(
                    "transition",
                    "transform ${t(300)}ms $Ease, " +
                            "border-color ${t(220)}ms ease, " +
                            "box-shadow ${t(300)}ms ease"
                )
            }.toAttrs()
    ) {
        Span(
            Modifier
                .styleModifier {
                    property("display", "inline-flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                    property("width", "40px")
                    property("height", "40px")
                    property("flex-shrink", "0")
                    property("border-radius", "12px")
                    property(
                        "background-image",
                        if (hovered)
                            "linear-gradient(135deg, ${GradeMateColors.Primary}, #4f46e5)"
                        else
                            "linear-gradient(135deg, ${GradeMateColors.Primary.toRgba(alpha = 0.12f)}, ${GradeMateColors.Primary.toRgba(alpha = 0.06f)})"
                    )
                    property(
                        "color",
                        if (hovered) "#ffffff" else GradeMateColors.Primary.toRgba()
                    )
                    property(
                        "transform",
                        if (hovered) "rotate(-6deg) scale(1.06)" else "rotate(0deg) scale(1)"
                    )
                    property(
                        "box-shadow",
                        if (hovered)
                            "0 10px 20px -8px ${GradeMateColors.Primary.toRgba(alpha = 0.6f)}"
                        else "none"
                    )
                    property(
                        "transition",
                        "background-image ${t(250)}ms ease, " +
                                "color ${t(250)}ms ease, " +
                                "transform ${t(400)}ms $EaseOutBack, " +
                                "box-shadow ${t(280)}ms ease"
                    )
                }
                .toAttrs()
        ) {
            FaIcon(
                name = iconName,
                style = IconCategory.SOLID,
                modifier = Modifier.fontSize(16.px)
            )
        }

        P(
            Modifier
                .margin(0.px)
                .fontSize(14.px)
                .fontWeight(FontWeight.SemiBold)
                .color(GradeMateColors.TextPrimary)
                .styleModifier { property("letter-spacing", "-0.005em") }
                .toAttrs()
        ) {
            Text(label)
        }
    }
}

// ---------------------------------------------------------------------------
// Requirement row
// ---------------------------------------------------------------------------
@Composable
private fun RequirementRow(
    iconName: String,
    label: String,
    value: String,
    divider: Boolean
) {
    Div(
        Modifier
            .styleModifier {
                property("display", "flex")
                property("align-items", "flex-start")
                property("gap", "18px")
                property("padding", "18px 0")
                if (divider) property("border-top", "1px solid ${GradeMateColors.Border.toRgba()}")
            }
            .toAttrs()
    ) {
        Span(
            Modifier
                .styleModifier {
                    property("display", "inline-flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                    property("width", "42px")
                    property("height", "42px")
                    property("flex-shrink", "0")
                    property("border-radius", "12px")
                    property(
                        "background-image",
                        "linear-gradient(135deg, ${GradeMateColors.Primary.toRgba(alpha = 0.12f)}, ${GradeMateColors.Primary.toRgba(alpha = 0.04f)})"
                    )
                    property("color", GradeMateColors.Primary.toRgba())
                    property(
                        "box-shadow",
                        "inset 0 1px 0 rgba(255,255,255,0.6)"
                    )
                }
                .toAttrs()
        ) {
            FaIcon(
                name = iconName,
                style = IconCategory.SOLID,
                modifier = Modifier.fontSize(16.px)
            )
        }

        Div(Modifier.fillMaxWidth().toAttrs()) {
            P(
                Modifier
                    .margin(0.px)
                    .fontSize(11.px)
                    .fontWeight(FontWeight.Bold)
                    .color(GradeMateColors.TextSecondary)
                    .styleModifier {
                        property("letter-spacing", "0.08em")
                        property("text-transform", "uppercase")
                        property("opacity", "0.7")
                    }
                    .toAttrs()
            ) {
                Text(label)
            }
            P(
                Modifier
                    .margin(top = 6.px, bottom = 0.px)
                    .fontSize(15.px)
                    .lineHeight(1.65)
                    .fontWeight(FontWeight.Medium)
                    .color(GradeMateColors.TextPrimary)
                    .styleModifier { property("letter-spacing", "-0.005em") }
                    .toAttrs()
            ) {
                Text(value)
            }
        }
    }
}

// ---------------------------------------------------------------------------
// Section
// ---------------------------------------------------------------------------
@Composable
fun DocumentationSection() {
    var selectedPlatform by remember {
        val detected = detectPlatformLabel()
        mutableStateOf(
            Platform.entries.firstOrNull { it.label.equals(detected, ignoreCase = true) } ?: Platform.Android
        )
    }
    var ctaHovered by remember { mutableStateOf(false) }
    val reduce = remember { prefersReducedMotion() }
    fun t(ms: Int) = if (reduce) 0 else ms

    Box(
        SectionStyle
            .toModifier()
            .id("docs")
            .backgroundColor(GradeMateColors.Surface)
            .styleModifier {
                property("position", "relative")
                property("overflow", "hidden")

                property(
                    "background-image",
                    "radial-gradient(45% 55% at 92% 6%, ${GradeMateColors.Primary.toRgba(alpha = 0.06f)}, transparent 70%), " +
                            "radial-gradient(40% 50% at 4% 92%, ${GradeMateColors.Primary.toRgba(alpha = 0.04f)}, transparent 70%)"
                )
            }
    ) {
        // Dotted grid
        Div(
            Modifier
                .styleModifier {
                    property("position", "absolute")
                    property("top", "0")
                    property("left", "0")
                    property("right", "0")
                    property("height", "520px")
                    property("pointer-events", "none")
                    property(
                        "background-image",
                        "radial-gradient(${GradeMateColors.Primary.toRgba(alpha = 0.14f)} 1px, transparent 1px)"
                    )
                    property("background-size", "28px 28px")
                    property("-webkit-mask-image", "linear-gradient(to bottom, black 40%, transparent)")
                    property("mask-image", "linear-gradient(to bottom, black 40%, transparent)")
                }.toAttrs()
        )

        Column(
            SectionInnerStyle
                .toModifier()
                .fillMaxWidth()
                .styleModifier {
                    property("position", "relative")
                    property("z-index", "1")
                }
        ) {

            // ---------------------------------------------------------
            // HEADER
            // ---------------------------------------------------------
            Reveal("docs-header") {
                Column(Modifier.fillMaxWidth()) {

                    Div(
                        Modifier
                            .styleModifier {
                                property("display", "inline-flex")
                                property("align-items", "center")
                                property("gap", "10px")
                            }.toAttrs()
                    ) {
                        Div(
                            Modifier
                                .styleModifier {
                                    property("width", "24px")
                                    property("height", "1px")
                                    property("background-color", GradeMateColors.Primary.toRgba(alpha = 0.4f))
                                }.toAttrs()
                        ) {}

                        P(
                            EyebrowStyle
                                .toModifier()
                                .margin(0.px)
                                .styleModifier {
                                    property("letter-spacing", "0.16em")
                                    property("text-transform", "uppercase")
                                    property("font-weight", "700")
                                    property("font-size", "12px")
                                }.toAttrs()
                        ) {
                            Text("Documentation")
                        }
                    }

                    P(
                        SectionHeadingStyle.toModifier()
                            .margin(top = 16.px, bottom = 18.px)
                            .styleModifier {
                                property("font-size", "clamp(32px, 4.5vw, 50px)")
                                property("letter-spacing", "-0.04em")
                                property("line-height", "1.05")
                                property("font-weight", "750")
                                property("max-width", "820px")
                            }.toAttrs()
                    ) {
                        Text("Everything you need to get started")
                    }

                    P(
                        Modifier
                            .margin(0.px)
                            .fontSize(16.px)
                            .lineHeight(1.7)
                            .color(GradeMateColors.TextSecondary)
                            .styleModifier {
                                property("max-width", "560px")
                                property("opacity", "0.82")
                            }.toAttrs()
                    ) {
                        Text(
                            "Set up GradeMate on your device in a couple of minutes. " +
                                    "Pick your platform and follow along."
                        )
                    }

                    Div(
                        Modifier
                            .margin(top = 28.px)
                            .styleModifier {
                                property("display", "flex")
                                property("flex-wrap", "wrap")
                                property("gap", "10px")
                            }.toAttrs()
                    ) {
                        DocsPill("Overview", "#docs-welcome")
                        DocsPill("Requirements", "#docs-requirements")
                        DocsPill("Download", "#docs-cta")
                    }
                }
            }

            // ---------------------------------------------------------
            // 01 · OVERVIEW
            // ---------------------------------------------------------
            Reveal("docs-welcome", Modifier.margin(top = 56.px), delayMs = 100) {
                Box(
                    Modifier
                        .fillMaxWidth()
                        .styleModifier {
                            property("position", "relative")
                            property("padding", "clamp(28px, 4.5vw, 44px)")
                            property("border-radius", "26px")
                            property(
                                "background-image",
                                "linear-gradient(155deg, rgba(255,255,255,1) 0%, rgba(250, 251, 253, 0.98) 100%)"
                            )
                            property("border", "1px solid ${GradeMateColors.Border.toRgba()}")
                            property(
                                "box-shadow",
                                "0 40px 80px -48px rgba(15, 23, 42, 0.35), " +
                                        "0 2px 8px -4px rgba(15, 23, 42, 0.06), " +
                                        "inset 0 1px 0 rgba(255, 255, 255, 0.9)"
                            )
                            property("overflow", "hidden")
                        }
                ) {
                    Div(
                        Modifier
                            .styleModifier {
                                property("position", "absolute")
                                property("top", "-30%")
                                property("right", "-15%")
                                property("width", "50%")
                                property("aspect-ratio", "1")
                                property("border-radius", "50%")
                                property(
                                    "background",
                                    "radial-gradient(circle, ${GradeMateColors.Primary.toRgba(alpha = 0.08f)}, transparent 70%)"
                                )
                                property("pointer-events", "none")
                            }.toAttrs()
                    )

                    Column(
                        Modifier
                            .fillMaxWidth()
                            .styleModifier { property("position", "relative") }
                    ) {

                        SubHeading("01", "Welcome to GradeMate")

                        P(
                            Modifier
                                .fillMaxWidth()
                                .margin(top = 22.px, bottom = 0.px)
                                .fontSize(15.px)
                                .lineHeight(1.85)
                                .color(GradeMateColors.TextSecondary)
                                .styleModifier {
                                    property("text-align", "justify")
                                    property("hyphens", "auto")
                                    property("max-width", "900px")
                                }.toAttrs()
                        ) {
                            Text(
                                "GradeMate is a comprehensive academic management platform " +
                                        "designed to streamline grading, coursework, and student " +
                                        "performance tracking. It provides powerful tools for tutors " +
                                        "to manage modules, record grades, and monitor progress, " +
                                        "while giving students a clear view of their results and " +
                                        "coursework submissions."
                            )
                        }

                        Div(
                            HighlightTileGridStyle
                                .toModifier()
                                .margin(top = 32.px)
                                .toAttrs()
                        ) {
                            HighlightTile("layer-group", "Manage modules")
                            HighlightTile("clipboard-check", "Record grades")
                            HighlightTile("chart-line", "Track progress")
                            HighlightTile("graduation-cap", "View results")
                            HighlightTile("users", "Manage students")
                            HighlightTile("chalkboard-user", "Manage tutors")
                            HighlightTile("file-lines", "Get transcripts")
                            HighlightTile("award", "Certificates")
                        }

                        Box(
                            Modifier
                                .margin(top = 32.px)
                                .styleModifier {
                                    property("display", "flex")
                                    property("align-items", "flex-start")
                                    property("gap", "14px")
                                    property("padding", "16px 20px")
                                    property("border-radius", "14px")
                                    property(
                                        "background-image",
                                        "linear-gradient(135deg, ${GradeMateColors.Primary.toRgba(alpha = 0.08f)}, ${GradeMateColors.Primary.toRgba(alpha = 0.03f)})"
                                    )
                                    property("border", "1px solid ${GradeMateColors.Primary.toRgba(alpha = 0.18f)}")
                                }
                        ) {
                            Span(
                                Modifier
                                    .styleModifier {
                                        property("display", "inline-flex")
                                        property("align-items", "center")
                                        property("justify-content", "center")
                                        property("width", "28px")
                                        property("height", "28px")
                                        property("flex-shrink", "0")
                                        property("border-radius", "8px")
                                        property("background-color", GradeMateColors.Primary.toRgba(alpha = 0.15f))
                                        property("color", GradeMateColors.Primary.toRgba())
                                    }.toAttrs()
                            ) {
                                FaIcon(
                                    name = "circle-info",
                                    style = IconCategory.SOLID,
                                    modifier = Modifier.fontSize(13.px)
                                )
                            }
                            P(
                                Modifier
                                    .margin(0.px)
                                    .fontSize(14.px)
                                    .lineHeight(1.7)
                                    .color(GradeMateColors.TextSecondary)
                                    .toAttrs()
                            ) {
                                Text(
                                    "Before installing, ensure your system meets the " +
                                            "requirements below."
                                )
                            }
                        }
                    }
                }
            }

            // ---------------------------------------------------------
            // 02 · INSTALLATION REQUIREMENTS
            // ---------------------------------------------------------
            Reveal("docs-requirements", Modifier.margin(top = 88.px)) {
                Column(Modifier.fillMaxWidth()) {

                    SubHeading(
                        "02",
                        "Installation requirements",
                        "GradeMate is available across multiple platforms. Select yours below to view the minimum specifications."
                    )

                    val platforms = Platform.entries
                    val selectedIndex = platforms.indexOf(selectedPlatform)

                    Div(
                        Modifier
                            .margin(top = 28.px)
                            .styleModifier {
                                property("position", "relative")
                                property("display", "inline-grid")
                                property("grid-auto-flow", "column")
                                property("grid-auto-columns", "1fr")
                                property("padding", "5px")
                                property("border-radius", "16px")
                                property(
                                    "background-image",
                                    "linear-gradient(180deg, rgba(248, 250, 252, 1), rgba(241, 245, 249, 0.8))"
                                )
                                property("border", "1px solid ${GradeMateColors.Border.toRgba()}")
                                property(
                                    "box-shadow",
                                    "inset 0 1px 2px rgba(15, 23, 42, 0.03)"
                                )
                                property("max-width", "100%")
                                property("overflow-x", "auto")
                                property("align-self", "flex-start")
                            }.toAttrs()
                    ) {
                        Div(
                            Modifier
                                .styleModifier {
                                    property("position", "absolute")
                                    property("top", "5px")
                                    property("bottom", "5px")
                                    property("left", "5px")
                                    property("width", "calc((100% - 10px) / ${platforms.size})")
                                    property("border-radius", "12px")
                                    property(
                                        "background-image",
                                        "linear-gradient(135deg, ${GradeMateColors.Primary}, #4f46e5)"
                                    )
                                    property(
                                        "box-shadow",
                                        "0 12px 24px -8px ${GradeMateColors.Primary.toRgba(alpha = 0.6f)}, " +
                                                "inset 0 1px 0 rgba(255, 255, 255, 0.25)"
                                    )
                                    property("transform", "translateX(${selectedIndex * 100}%)")
                                    property("transition", "transform ${t(420)}ms $Ease")
                                }.toAttrs()
                        )

                        platforms.forEach { platform ->
                            val isSelected = platform == selectedPlatform
                            val (iconName, iconCategory) = platformIcon(platform.label)

                            Button(
                                attrs = Modifier
                                    .onClick { selectedPlatform = platform }
                                    .styleModifier {
                                        property("position", "relative")
                                        property("z-index", "1")
                                        property("display", "inline-flex")
                                        property("align-items", "center")
                                        property("justify-content", "center")
                                        property("gap", "9px")
                                        property("padding", "10px 22px")
                                        property("border", "0")
                                        property("border-radius", "12px")
                                        property("background", "transparent")
                                        property("cursor", "pointer")
                                        property("font-family", "inherit")
                                        property("font-size", "14px")
                                        property("font-weight", "600")
                                        property("white-space", "nowrap")
                                        property(
                                            "color",
                                            if (isSelected) "#ffffff" else GradeMateColors.TextSecondary.toRgba()
                                        )
                                        property(
                                            "transition",
                                            "color ${t(250)}ms ease"
                                        )
                                    }
                                    .toAttrs {
                                        attr("type", "button")
                                        attr("aria-pressed", isSelected.toString())
                                    }
                            ) {
                                FaIcon(
                                    name = iconName,
                                    style = iconCategory,
                                    modifier = Modifier.fontSize(14.px)
                                )
                                Text(platform.label)
                            }
                        }
                    }

                    Column(
                        CardStyle
                            .toModifier()
                            .margin(top = 24.px)
                            .fillMaxWidth()
                            .styleModifier {
                                property("position", "relative")
                                property("overflow", "hidden")
                                property("padding", "clamp(24px, 3.5vw, 36px)")
                                property("max-width", "720px")
                                property("border-radius", "22px")
                                property(
                                    "background-image",
                                    "linear-gradient(155deg, rgba(255,255,255,1) 0%, rgba(250, 251, 253, 0.98) 100%)"
                                )
                                property(
                                    "box-shadow",
                                    "0 40px 80px -48px rgba(15, 23, 42, 0.3), " +
                                            "0 2px 8px -4px rgba(15, 23, 42, 0.05), " +
                                            "inset 0 1px 0 rgba(255, 255, 255, 0.9)"
                                )
                            }
                    ) {
                        key(selectedPlatform) {
                            var shown by remember { mutableStateOf(false) }
                            LaunchedEffect(Unit) {
                                delay(20.milliseconds)
                                shown = true
                            }
                            val (markName, markCategory) = platformIcon(selectedPlatform.label)

                            Column(
                                Modifier
                                    .fillMaxWidth()
                                    .styleModifier {
                                        property("position", "relative")
                                        property("opacity", if (shown) "1" else "0")
                                        property("transform", if (shown) "translateY(0)" else "translateY(10px)")
                                        property(
                                            "transition",
                                            "opacity ${t(400)}ms ease, transform ${t(500)}ms $Ease"
                                        )
                                    }
                            ) {
                                FaIcon(
                                    name = markName,
                                    style = markCategory,
                                    modifier = Modifier
                                        .fontSize(170.px)
                                        .styleModifier {
                                            property("position", "absolute")
                                            property("right", "-22px")
                                            property("top", "-44px")
                                            property("color", GradeMateColors.Primary.toRgba(alpha = 0.06f))
                                            property("pointer-events", "none")
                                            property("transform", "rotate(-6deg)")
                                        }
                                )

                                Div(
                                    Modifier
                                        .margin(bottom = 10.px)
                                        .styleModifier {
                                            property("position", "relative")
                                            property("display", "flex")
                                            property("align-items", "center")
                                            property("gap", "14px")
                                        }.toAttrs()
                                ) {
                                    Span(
                                        Modifier
                                            .styleModifier {
                                                property("display", "inline-flex")
                                                property("align-items", "center")
                                                property("justify-content", "center")
                                                property("width", "40px")
                                                property("height", "40px")
                                                property("border-radius", "12px")
                                                property(
                                                    "background-image",
                                                    "linear-gradient(135deg, ${GradeMateColors.Primary}, #4f46e5)"
                                                )
                                                property("color", "#ffffff")
                                                property(
                                                    "box-shadow",
                                                    "0 8px 18px -8px ${GradeMateColors.Primary.toRgba(alpha = 0.65f)}"
                                                )
                                            }.toAttrs()
                                    ) {
                                        FaIcon(
                                            name = markName,
                                            style = markCategory,
                                            modifier = Modifier.fontSize(17.px)
                                        )
                                    }
                                    P(
                                        Modifier
                                            .margin(0.px)
                                            .fontSize(19.px)
                                            .fontWeight(FontWeight.Bold)
                                            .color(GradeMateColors.TextPrimary)
                                            .styleModifier {
                                                property("letter-spacing", "-0.02em")
                                                property("line-height", "1.2")
                                            }.toAttrs()
                                    ) {
                                        Text("${selectedPlatform.label} requirements")
                                    }
                                }

                                Div(
                                    Modifier
                                        .fillMaxWidth()
                                        .styleModifier { property("position", "relative") }.toAttrs()
                                ) {
                                    RequirementRow("desktop", "Operating system", selectedPlatform.minOs, divider = false)
                                    RequirementRow("database", "Storage", selectedPlatform.storage, divider = true)
                                    RequirementRow("microchip", "Other", selectedPlatform.extra, divider = true)
                                }
                            }
                        }
                    }
                }
            }

            // ---------------------------------------------------------
            // DOWNLOAD CTA
            // ---------------------------------------------------------
            Reveal("docs-cta", Modifier.margin(top = 80.px)) {
                Box(
                    Modifier
                        .fillMaxWidth()
                        .styleModifier {
                            property("position", "relative")
                            property("overflow", "hidden")
                            property("padding", "clamp(40px, 6vw, 64px) clamp(28px, 4.5vw, 48px)")
                            property("border-radius", "28px")
                            property(
                                "background-image",
                                "linear-gradient(135deg, ${GradeMateColors.Primary} 0%, #4338ca 100%)"
                            )
                            property(
                                "box-shadow",
                                "0 44px 80px -36px ${GradeMateColors.Primary.toRgba(alpha = 0.7f)}, " +
                                        "inset 0 1px 0 rgba(255, 255, 255, 0.25)"
                            )
                        }
                ) {
                    Box(
                        Modifier.styleModifier {
                            property("position", "absolute")
                            property("top", "-40%")
                            property("right", "-10%")
                            property("width", "55%")
                            property("aspect-ratio", "1")
                            property("border-radius", "50%")
                            property(
                                "background",
                                "radial-gradient(circle, rgba(255,255,255,0.22), rgba(255,255,255,0) 70%)"
                            )
                            property("pointer-events", "none")
                        }
                    )
                    Box(
                        Modifier.styleModifier {
                            property("position", "absolute")
                            property("bottom", "-50%")
                            property("left", "-8%")
                            property("width", "45%")
                            property("aspect-ratio", "1")
                            property("border-radius", "50%")
                            property(
                                "background",
                                "radial-gradient(circle, rgba(255,255,255,0.12), rgba(255,255,255,0) 70%)"
                            )
                            property("pointer-events", "none")
                        }
                    )
                    Box(
                        Modifier.styleModifier {
                            property("position", "absolute")
                            property("top", "0")
                            property("left", "0")
                            property("right", "0")
                            property("height", "1px")
                            property("background", "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)")
                            property("pointer-events", "none")
                        }
                    )

                    Column(
                        Modifier
                            .fillMaxWidth()
                            .styleModifier {
                                property("position", "relative")
                                property("z-index", "1")
                            },
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {

                        Span(
                            Modifier
                                .styleModifier {
                                    property("display", "inline-flex")
                                    property("align-items", "center")
                                    property("gap", "8px")
                                    property("padding", "7px 14px")
                                    property("border-radius", "999px")
                                    property("background-color", "rgba(255, 255, 255, 0.18)")
                                    property("border", "1px solid rgba(255, 255, 255, 0.25)")
                                    property("color", "#ffffff")
                                    property("font-size", "11px")
                                    property("font-weight", "700")
                                    property("letter-spacing", "0.08em")
                                    property("text-transform", "uppercase")
                                    property("margin-bottom", "20px")
                                }.toAttrs()
                        ) {
                            Text("Available now")
                        }

                        P(
                            Modifier
                                .margin(0.px)
                                .fontWeight(FontWeight.Bold)
                                .color(Colors.White)
                                .styleModifier {
                                    property("font-size", "clamp(26px, 3.5vw, 38px)")
                                    property("letter-spacing", "-0.035em")
                                    property("line-height", "1.15")
                                    property("text-align", "center")
                                    property("max-width", "700px")
                                }.toAttrs()
                        ) {
                            Text("Ready to get started?")
                        }

                        P(
                            Modifier
                                .margin(top = 14.px, bottom = 0.px)
                                .fontSize(16.px)
                                .lineHeight(1.7)
                                .color(Colors.White)
                                .styleModifier {
                                    property("opacity", "0.92")
                                    property("text-align", "center")
                                    property("max-width", "540px")
                                }.toAttrs()
                        ) {
                            Text(
                                "Download GradeMate for your platform and start " +
                                        "managing your academic records with ease."
                            )
                        }

                        Link(
                            path = "#downloads",
                            text = "Download for ${selectedPlatform.label} →",
                            modifier = SecondaryButtonStyle
                                .toModifier()
                                .margin(top = 32.px)
                                .onMouseEnter { ctaHovered = true }
                                .onMouseLeave { ctaHovered = false }
                                .styleModifier {
                                    property("display", "inline-flex")
                                    property("align-items", "center")
                                    property("justify-content", "center")
                                    property("gap", "10px")
                                    property("height", "54px")
                                    property("box-sizing", "border-box")
                                    property("padding", "0 32px")
                                    property("border-radius", "14px")
                                    property("text-decoration", "none")
                                    property("font-size", "15px")
                                    property("font-weight", "650")
                                    property("letter-spacing", "-0.01em")
                                    property("line-height", "1")
                                    property("color", GradeMateColors.Primary.toRgba())
                                    property("background-color", "#ffffff")
                                    property("border", "0")
                                    property(
                                        "box-shadow",
                                        if (ctaHovered)
                                            "0 24px 44px -12px rgba(15, 23, 42, 0.55), " +
                                                    "inset 0 1px 0 rgba(255, 255, 255, 0.9)"
                                        else
                                            "0 10px 24px -8px rgba(15, 23, 42, 0.4), " +
                                                    "inset 0 1px 0 rgba(255, 255, 255, 0.9)"
                                    )
                                    property(
                                        "transform",
                                        if (ctaHovered) "translateY(-3px)" else "translateY(0)"
                                    )
                                    property(
                                        "transition",
                                        "transform ${t(250)}ms $Ease, box-shadow ${t(250)}ms ease"
                                    )
                                }
                        )
                    }
                }
            }
        }
    }
}